import React, {
  useRef,
  useEffect,
  useReducer,
  memo,
  useCallback,
  useState,
  useMemo
} from "react";
import ReactTooltip from "react-tooltip";
import noop from "lodash/noop";
import debounce from "lodash/debounce";

import useFieldFast from "../../../hooks/useFieldFast";
import { allIncluded } from "../../../utils";

import Spinner from "../../Spinner";
import SearchInputLabel from "./label";
import ErrorIcon from "../../icons/errorIcon";
import ArrowUpIcon from "../../icons/arrowUpIcon";
import ArrowDownIcon from "../../icons/arrowDownIcon";
import ClearInputIcon from "../../icons/clearInputIcon";
import SearchIcon from "../../icons/searchIcon";

import { searchInputReducer, ACTIONS } from "./reducer";
import { fetchCatalogBy } from "../../../api/catalogService";
import { useSessionContext } from "../../../contexts/sessionContext";
import { withTranslation, useTranslation } from 'react-i18next';

const getInitialState = ({
  options,
  multiple,
  optionTextName,
  optionValueName,
  displayAliasText
}) => ({
  activeOption: 0,
  filteredOptions: [],
  values: [],
  options:
    optionTextName && optionValueName
      ? options.map((o) => ({
        text: o[optionTextName],
        value: o[optionValueName]
      }))
      : options,
  userInput: "",
  showOptions: false,
  hasError: false,
  touched: false,
  focused: false,
  inputValue: "",
  valuePayload: null,
  displayAliasText,
  multiple,
  defaultOption: null
});

const SearchInput = (props) => {
  const { t } = useTranslation();

  const {
    name,
    label,
    labelButton,
    options,
    hintText,
    optionTextName,
    optionValueName,
    placeHolder,
    multiple,
    icon,
    className,
    containerClassName,
    showErrorText,
    showErrorIcon,
    inputAside,
    checkbox,
    inputAsideProps = {},
    clearOnChange,
    disabled,
    displayAliasText,
    onChange: onCustomOnChange,
    onInputChange,
    onBlur: onCustomBlur,
    multipleInputProps = {},
    enableLeadingButton,
    leadingButtonProps = {},
    usePhysicalTouched,
    showItemEditOption,
    onEditItemClickHandler,
    onDeleteItemClickHandler,
    readOnly,
    fetchBy,
    emptySearchMessage,
    defaultOption,
    tooltipText,
    searchIcon,
    size = 'bg',
    initialOptions = true,
    reference,
    globalParties,
    warningIcon,
    warningMessage
  } = props;

  const { maxItems: maxMultipleItems } = multipleInputProps;
  const {
    value: inputAsideValue = null,
    hasError: hasInputAsideErrors = false,
    errorMessage: inputAsideErrorMessage = null
  } = inputAsideProps;

  const {
    onClick: onLeadingButtonClick = noop,
    label: leadingButtonLabel = ""
  } = leadingButtonProps;

  const initialState = useMemo(() =>
    getInitialState({
      options,
      multiple,
      optionTextName,
      optionValueName,
      displayAliasText
    }),
    [options, multiple, optionTextName, optionValueName, displayAliasText]
  );

  const [state, dispatch] = useReducer(searchInputReducer, initialState);
  const [field, meta, helpers] = useFieldFast(name);
  const { value, error, touched } = meta;

  const [isLoading, setIsLoading] = useState(false);
  const loadingTimeoutRef = useRef(null);

  const lastInputValueRef = useRef("");
  const isSettingValueRef = useRef(false);
  
  // 📌 NUEVOS REFS PARA CONTROLAR EL LOOP
  const initialLoadDoneRef = useRef(false);
  const prevDefaultOptionRef = useRef(null);
  const prevValueRef = useRef(null);
  const processingDefaultRef = useRef(false);

  const { setValue, setTouched } = helpers;

  const wrapperRef = reference ? reference : useRef(null);
  const inputRef = useRef(null);

  const {
    token,
    currentOrganization: { id: currentOrganizationId },
    userData
  } = useSessionContext();

  const isTouched = usePhysicalTouched ? state.touched : touched;

  const updateDispatch = useCallback((value) => {
    dispatch({
      type: ACTIONS.UPDATE,
      value
    });
  }, []);

  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (state.inputValue !== lastInputValueRef.current && !isSettingValueRef.current) {
      lastInputValueRef.current = state.inputValue;
      setValue(state.inputValue);
      onCustomOnChange(state.inputValue, state.userInput, state.valuePayload);
    }
  }, [state.inputValue, state.userInput, state.valuePayload, setValue, onCustomOnChange]);

  useEffect(() => {
    if (clearOnChange && state.inputValue && state.inputValue !== lastInputValueRef.current) {
      updateDispatch({ userInput: "" });
    }
  }, [state.inputValue, clearOnChange]);

  // Reset initialLoadDoneRef when defaultOption changes, so the new default is applied
  useEffect(() => {
    if (prevDefaultOptionRef.current !== defaultOption) {
      initialLoadDoneRef.current = false;
      prevDefaultOptionRef.current = defaultOption;
    }
  }, [defaultOption]);

  // Reset when the form is reinitialized (e.g. modal closed and reopened).
  // Two signals are used:
  // 1. isTouched transitions back to false after being true (Formik's enableReinitialize reset)
  // 2. value resets to match the original defaultOption value while isTouched is false
  //    (covers usePhysicalTouched mode where state.touched may not reset automatically)
  const prevIsTouchedRef = useRef(false);
  useEffect(() => {
    if (prevIsTouchedRef.current === true && isTouched === false) {
      initialLoadDoneRef.current = false;
      prevValueRef.current = null;
    }
    prevIsTouchedRef.current = isTouched;
  }, [isTouched]);

  const prevFormikValueRef = useRef(value);
  useEffect(() => {
    // If the external form value changed while the field is not touched (form reset),
    // force re-evaluation of the default option.
    if (!isTouched && prevFormikValueRef.current !== value && initialLoadDoneRef.current) {
      initialLoadDoneRef.current = false;
      prevValueRef.current = null;
    }
    prevFormikValueRef.current = value;
  }, [value, isTouched]);

  // useEffect for defaultOption / fetchBy initial load
  useEffect(() => {
    // Prevent concurrent processing
    if (processingDefaultRef.current) return;

    // Only relevant when fetchBy is configured
    if (!fetchBy || !fetchBy?.type) return;

    // Skip if already processed for the same value + defaultOption combination
    if (prevValueRef.current === value && initialLoadDoneRef.current) {
      return;
    }

    const { type, dataKey, valueKey, mapStrategy, fetchDefault } = fetchBy;
    
    // Solo procesar si hay value o defaultOption
    if (value && !isTouched) {
      if (valueKey || fetchDefault) {
        const searchParams = {
          text: "",
          searchKey: "",
          searchValue: ""
        };
        if (fetchDefault) {
          searchParams.searchKey = fetchDefault.searchKey;
          searchParams.searchValue = fetchDefault.searchValue;
        } else {
          searchParams.text = value;
          searchParams.searchKey = valueKey;
        }

        processingDefaultRef.current = true;
        setIsLoading(true);
        
        fetchCatalogBy(token, currentOrganizationId, type, searchParams)
          .then((response) => {
            const data = response[dataKey];
            const options = data.map(mapStrategy);
            const [option] = options.filter((e) => e != null);
            if (option && !isSettingValueRef.current) {
              isSettingValueRef.current = true;
              updateDispatch({
                userInput: option?.aliasText || option?.text || "",
                inputValue: option?.value || "",
                options: [option],
                valuePayload: option
              });
              isSettingValueRef.current = false;
              
              // 📌 GUARDAR REFERENCIAS
              prevValueRef.current = value;
              initialLoadDoneRef.current = true;
            }
          })
          .finally(() => {
            setIsLoading(false);
            processingDefaultRef.current = false;
          });
      } else if (defaultOption && !initialLoadDoneRef.current) {
        // 📌 SOLO PROCESAR UNA VEZ
        if (Array.isArray(defaultOption) && defaultOption.length > 0) {
          if (value === defaultOption.map(d => d.value).join(",")) {
            processingDefaultRef.current = true;
            updateDispatch({
              values: defaultOption,
              inputValue: defaultOption.map(d => d.value).join(",")
            });
            processingDefaultRef.current = false;
            initialLoadDoneRef.current = true;
            prevDefaultOptionRef.current = defaultOption;
            prevValueRef.current = value;
          }
        } else {
          processingDefaultRef.current = true;
          isSettingValueRef.current = true;

          updateDispatch({
            userInput: defaultOption?.aliasText || defaultOption?.text || "",
            inputValue: defaultOption?.value || "",
            options: [defaultOption],
            defaultOption,
            valuePayload: defaultOption
          });
          
          isSettingValueRef.current = false;
          processingDefaultRef.current = false;
          initialLoadDoneRef.current = true;
          prevDefaultOptionRef.current = defaultOption;
          prevValueRef.current = value;
        }
      }
    } else if (!value && !isTouched && defaultOption && !initialLoadDoneRef.current) {
      // 📌 SOLO PROCESAR UNA VEZ CUANDO NO HAY VALUE
      processingDefaultRef.current = true;
      
      if (Array.isArray(defaultOption) && defaultOption.length > 0) {
        updateDispatch({
          userInput: "",
          options: defaultOption,
          defaultOption,
          inputValue: defaultOption.map(d => d.value).join(",")
        });
      } else {
        isSettingValueRef.current = true;
        updateDispatch({
          userInput: defaultOption?.aliasText || defaultOption?.text || "",
          inputValue: defaultOption?.value || "",
          options: [defaultOption],
          defaultOption,
          valuePayload: defaultOption
        });
        isSettingValueRef.current = false;
      }
      
      processingDefaultRef.current = false;
      initialLoadDoneRef.current = true;
      prevDefaultOptionRef.current = defaultOption;
      prevValueRef.current = value;
    }
  }, [value, isTouched, defaultOption, fetchBy, token, currentOrganizationId, updateDispatch]);

  useEffect(() => {
    if (fetchBy?.type) return;
    if (state && state.options && state.options.length !== options.length) {
      const optionsFormatted =
        optionTextName && optionValueName
          ? options.map((o) => ({
            text: o[optionTextName],
            value: o[optionValueName]
          }))
          : options;
      updateDispatch({ options: optionsFormatted });
    } else if (state && state.options && state.options.length) {
      if (multiple && value) {
        const selectedValues = value.split(",");
        const defaultOptions = state.options.filter((o) =>
          selectedValues.includes(o.value)
        );

        if (
          !allIncluded(
            selectedValues,
            defaultOptions.map((d) => d.value)
          ) ||
          (value && !state.inputValue)
        ) {
          updateDispatch({
            values: defaultOptions,
            inputValue: value
          });
        }
      } else {
        if (initialOptions) {
          const option = state.options.find((option) => option.value === value);
          if (option) {
            isSettingValueRef.current = true;
            updateDispatch({
              userInput: option.text,
              inputValue: value
            });
            isSettingValueRef.current = false;
          }
        }
      }
    }
  }, [value, state.values, options, state.options, fetchBy, optionTextName, optionValueName, multiple, initialOptions, state.inputValue]);

  const handleClickOutside = useCallback((event) => {
    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target) &&
      !event.target.classList.contains("search-input-item")
    ) {
      updateDispatch({ showOptions: false });
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, false);
    return () => {
      document.removeEventListener("click", handleClickOutside, false);
    };
  }, []);

  useEffect(() => {
    const { values, multiple: stateMultiple, focused } = state;
    if (stateMultiple && focused && maxMultipleItems === values.length) {
      updateDispatch({
        focused: false,
        touched: true,
        showOptions: false
      });
    }
  }, [state, maxMultipleItems]);


  const fetchOptionsBy = useMemo(
    () => {
      let currentQuery = '';

      return debounce(
        async (query) => {

          currentQuery = query;

          const {
            type,
            dataKey,
            mapStrategy,
            customSearchKey,
            customSearchValue,
            customFetchFunction,
            isActive
          } = fetchBy;

          loadingTimeoutRef.current = setTimeout(() => {
            if (currentQuery === query) {
              setIsLoading(true);
            }
          }, 50);

          try {
            const fetchFunction = customFetchFunction
              ? customFetchFunction(token, currentOrganizationId, query, { isRoot: userData?.userInfo?.root }, isActive ? isActive : null)
              : fetchCatalogBy(token, currentOrganizationId, type, {
                text: query,
                searchKey: customSearchKey,
                searchValue: customSearchValue
              }, ...(type === 'parties' ? [globalParties] : []));

            const response = await fetchFunction;

            if (currentQuery !== query) {

              return;
            }

            const data = dataKey ? response[dataKey] : response;
            const options = data && data.length && data.map(mapStrategy);
            const validOptions = options && options.length ? options.filter((e) => e != null) : [];

            const defaultOptionExists = defaultOption && validOptions.some(option => {
            
              if (option.value && defaultOption.value) {
                return option.value === defaultOption.value;
              }
              
          
              if (option.payload?.partyId && defaultOption.payload?.partyId) {
                return option.payload.partyId === defaultOption.payload.partyId;
              }
              
       
              if (defaultOption.payload?.address1) {
                return (
                  option.payload?.address1 === defaultOption.payload?.address1 &&
                  option.payload?.city === defaultOption.payload?.city &&
                  option.payload?.stateProvince === defaultOption.payload?.stateProvince
                );
              }
              

              return option.text === defaultOption.text;
            });


            const shouldAddDefaultOption = defaultOption &&
              !defaultOptionExists &&
              validOptions.length < 20; 

            const optionsFormatted = shouldAddDefaultOption
              ? [{ ...defaultOption }, ...validOptions]
              : validOptions;

            updateDispatch({
              options: optionsFormatted,
              filteredOptions: optionsFormatted,
              showOptions: true
            });
          } catch (error) {

            if (currentQuery === query) {
              updateDispatch({
                options: [],
                filteredOptions: [],
                showOptions: true
              });
            }
          } finally {
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
              loadingTimeoutRef.current = null;
            }
            if (currentQuery === query) {
              setIsLoading(false);
            }
          }
        },
        80,
        {
          trailing: true,
          leading: true,
          maxWait: 300
        }
      );
    },
    [fetchBy, defaultOption, currentOrganizationId, globalParties, userData, token]
  );

  useEffect(() => {
    return () => {
      fetchOptionsBy.cancel?.();
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);


  const onChange = useCallback(({ currentTarget: { value: query } }) => {
    onInputChange?.(query);

    if (state.inputValue && query !== state.userInput) {
      isSettingValueRef.current = true;
      updateDispatch({
        inputValue: '',
        valuePayload: null
      });
      setValue('');
      isSettingValueRef.current = false;
    }

    updateDispatch({
      userInput: query,
      showOptions: true,
      focused: true,
      activeOption: 0
    });

    if (fetchBy?.type) {
      fetchOptionsBy(query);
    } else {
      setTouched(true);
      const filtered = state.options.filter(option =>
        option && option.text &&
        option.text.toString().toLowerCase().includes(query.toLowerCase())
      );
      updateDispatch({
        filteredOptions: filtered,
        activeOption: 0
      });
    }
  }, [fetchBy, fetchOptionsBy, state.options, state.inputValue, state.userInput, setValue, setTouched, onInputChange]);

  const onFocusInputHandler = useCallback(() => {

    setTouched(true);

    updateDispatch({
      focused: true,
      showOptions: true
    });

    if (fetchBy?.type) {
      const queryToSearch = state?.userInput || "";

      if (fetchOptionsBy.cancel) {
        fetchOptionsBy.cancel();
      }

      fetchOptionsBy(queryToSearch);

      if (fetchOptionsBy.flush) {

        fetchOptionsBy.flush();
      }

      setTimeout(() => {
        if (!isLoading && state.options.length === 0) {

          fetchOptionsBy(queryToSearch);
        }
      }, 100);
    }
  }, [fetchBy, fetchOptionsBy, state.userInput, state.options.length, isLoading, setTouched]);

  const clearValue = useCallback(() => {


    setTouched(true);
    onCustomBlur();


    fetchOptionsBy.cancel?.();


    isSettingValueRef.current = true;
    updateDispatch({
      userInput: '',
      inputValue: '',
      touched: true,
      focused: true,
      showOptions: true,
      activeOption: 0,
      valuePayload: null
    });
    isSettingValueRef.current = false;

    setValue('');


    if (fetchBy?.type) {

      fetchOptionsBy("");
    }
  }, [fetchBy, fetchOptionsBy, setValue, setTouched, onCustomBlur]);

  const onClickItemHandler = useCallback((item) => {
    setTouched(true);
    if (multiple) {
      const values = [...state.values, { ...item }];
      const newInputValue = values.map(v => v.value).join(",");

      isSettingValueRef.current = true;
      updateDispatch({
        activeOption: 0,
        values: values,
        userInput: "",
        showOptions: false,
        inputValue: newInputValue,
        valuePayload: item
      });
      isSettingValueRef.current = false;

      setValue(newInputValue);
    } else {
      const { text, value: itemValue } = item;

      isSettingValueRef.current = true;

      updateDispatch({
        activeOption: 0,
        userInput: text,
        inputValue: itemValue,
        showOptions: false,
        valuePayload: item
      });
      isSettingValueRef.current = false;

      setValue(itemValue);
    }
  }, [multiple, state.values, setValue, setTouched]);

  const onKeyDown = useCallback(({ keyCode }) => {
    const { activeOption, filteredOptions } = state;
    if (keyCode === 13) {
      const stateProps = {
        showOptions: false,
        activeOption: 0
      };
      const activeItem = filteredOptions[activeOption];
      if (multiple && activeItem) {
        const values = [...state.values, { ...activeItem }];
        const newInputValue = values.map(v => v.value).join(",");

        isSettingValueRef.current = true;

        updateDispatch({
          userInput: "",
          values: values,
          inputValue: newInputValue,
          ...stateProps
        });
        isSettingValueRef.current = false;

        setValue(newInputValue);
      } else if (activeItem) {
        isSettingValueRef.current = true;

        updateDispatch({
          userInput: activeItem?.text || "",
          inputValue: activeItem?.value || "",
          ...stateProps
        });
        isSettingValueRef.current = false;

        setValue(activeItem?.value || "");
      }
    } else if (keyCode === 38) {
      if (activeOption === 0) {
        return;
      }

      updateDispatch({ activeOption: activeOption - 1 });
    } else if (keyCode === 40) {
      if (activeOption === filteredOptions.length - 1) {
        return;
      }

      updateDispatch({ activeOption: activeOption + 1 });
    }
  }, [state.activeOption, state.filteredOptions, state.values, multiple, setValue]);

  const OptionsContainer = useCallback(({ children }) => {
    return (
      <ul className="z-10 absolute left-0 mt-1 w-full rounded-sm border border-inputFocus overflow-x-hidden overflow-y-auto max-h-48 search-input-options-wrapper">
        {enableLeadingButton && (
          <li
            className="px-4 py-3 search-input-item bg-blue-100 cursor-pointer font-semibold"
            onClick={onLeadingButtonClick}
          >
            {leadingButtonLabel}
          </li>
        )}
        {children}
      </ul>
    );
  }, [enableLeadingButton, onLeadingButtonClick, leadingButtonLabel]);

  const renderOptionsList = useCallback(({
    userInput,
    filteredOptions,
    options,
    activeOption
  }) => {
    if (!state.showOptions) {
      return null;
    }

    const emptyItemClassName =
      "z-50 px-4 py-3 hover:bg-blue-200 search-input-item bg-white";

    if (isLoading) {
      return (
        <OptionsContainer>
          <li className="z-50 px-4 py-2 search-input-item bg-white flex justify-center items-center">
            <Spinner inverted={true} size="small" />
            <span className="ml-2 text-xs text-gray-500">{t("Loading...")}</span>
          </li>
        </OptionsContainer>
      );
    }

    if (options && !options.length) {
      return (
        <OptionsContainer>
          <li className={emptyItemClassName}>{t(emptySearchMessage)}</li>
        </OptionsContainer>
      );
    }

    const displayOptions = filteredOptions && filteredOptions.length ? filteredOptions : options;

    if (userInput && displayOptions && !displayOptions.length) {
      return (
        <OptionsContainer>
          <li className={emptyItemClassName}>{t("No matches found")}</li>
        </OptionsContainer>
      );
    }

    const limitedOptions = displayOptions ? displayOptions.slice(0, 50) : [];

    return (
      <OptionsContainer>
        {limitedOptions
          .filter(option => option && option.text !== 0 && option.text !== null && option.text !== undefined && option.text !== '')
          .map((option, index) => {
            const { text } = option;
            const itemClassNames =
              "z-50 px-2 py-2 lg:px-4 lg:py-3 search-input-item cursor-pointer hover:bg-blue-200 bg-white text-2xs xl:text-xs";

            return (
              <li
                key={`${text}-${index}`}
                dataindex={index}
                onClick={() => onClickItemHandler(option)}
                className={itemClassNames}
              >
                {t(text)}
              </li>
            );
          })}
      </OptionsContainer>
    );
  }, [state.showOptions, isLoading, onClickItemHandler, t, emptySearchMessage]);

  const handleToggleOptions = useCallback(() => {
    if (disabled || maxMultipleItems === state.values.length) return;

    setTouched(true);

    updateDispatch({
      showOptions: !state.showOptions
    });

    if (!state.showOptions && fetchBy?.type) {

      fetchOptionsBy(state.userInput || "");
    }
  }, [disabled, maxMultipleItems, state.values.length, state.showOptions, fetchBy, fetchOptionsBy, state.userInput, setTouched]);

  const onDeleteChipHandler = useCallback((value) => {
    const item = state.values.find((item) => item.value === value);
    const itemIndex = state.values.indexOf(item);
    const newValues = [
      ...state.values.slice(0, itemIndex),
      ...state.values.slice(itemIndex + 1)
    ];
    const newInputValue = newValues.map(v => v.value).join(",");

    isSettingValueRef.current = true;

    updateDispatch({
      values: newValues,
      inputValue: newInputValue
    });
    isSettingValueRef.current = false;

    setValue(newInputValue);
    onDeleteItemClickHandler(value);
  }, [state.values, setValue, onDeleteItemClickHandler]);

  const onBlurInputHandler = useCallback(() => {
    onCustomBlur();

    updateDispatch({
      touched: true,
      focused: false
    });
  }, [onCustomBlur]);

  const getContainerClassNames = useCallback((
    { showOptions },
    focused,
    containerClassName,
    error
  ) => {
    const classNames = [
      "flex flex-row items-stretch rounded-sm border",
      containerClassName
    ];
    classNames.push(focused || showOptions ? "border-inputFocus" : "border");

    if (error && isTouched && !showOptions) {
      classNames.push("border-inputBorderError");
    }
    if (inputAside) {
      classNames.push("w-3/12");
    } else {
      classNames.push("w-full");
    }
    return classNames.join(" ");
  }, [isTouched, inputAside]);

  const { userInput, values, showOptions, focused, filteredOptions, options: stateOptions } = state;

  const containerClassNames = getContainerClassNames(
    state,
    focused,
    containerClassName,
    error
  );

  return (
    <div
      id={`search-input-${name}`}
      className={`search-input-wrapper w-full relative ${className}`}
      ref={wrapperRef}
    >
      {label && (
        <SearchInputLabel
          name={name}
          label={label}
          hintText={hintText}
          labelButton={labelButton}
          checkbox={checkbox}
          warningIcon={warningIcon}
          warningMessage={warningMessage}
        />
      )}
      {!readOnly ? (
        <div
          className={`wrapper relative inline-block text-xs w-full bg-white ${multiple ? "min-h-10" : size === 'sm' ? "h-6" : "h-10"
            } ${disabled ? "opacity-75" : ""}`}
        >
          <div className="flex w-full h-full">
            <div className={containerClassNames}>
              {icon && (
                <div className={` ${size === 'sm' ? 'h-3 mr-1 my-1 w-8' : 'h-3 mr-1 my-3 w-10'} relative`}>
                  <div className={`ml-4 absolute ${size === 'sm' ? 'text-xs' : ''}`}>{icon}</div>
                </div>
              )}
              <div className={`self-center ${size === 'sm' ? 'h-4' : 'h-full'} w-full`}>
                {multiple && (
                  <div className={`${values.length ? "py-2" : ""}`}>
                    {values.map(({ text, value: itemValue }, index) => {
                      return (
                        <div
                          key={`chip-${index}`}
                          className="w-auto inline-flex items-center h-6 bg-primary-400 rounded-full pl-3 pr-1 text-white text-xs mr-2 mb-2 last:mr-0 last:mb-0 leading-3"
                        >
                          <span
                            className="mr-2"
                            data-tip
                            data-for={`tooltip-${text}`}
                          >
                            {text}{" "}
                          </span>
                          {tooltipText ? (
                            <ReactTooltip
                              id={`tooltip-${text}`}
                              effect="solid"
                              place="right"
                              type="dark"
                              className="tooltipDrivers"
                            >
                              <span>{`License: ${tooltipText !== null && tooltipText[index]
                                ? tooltipText[index].licenseNumber
                                : "License not found"
                                }`}</span>
                            </ReactTooltip>
                          ) : null}
                          {showItemEditOption && (
                            <div
                              style={{
                                backgroundImage: "url(/icons/pencil.svg)"
                              }}
                              className="h-4 w-4 rounded-full bg-no-repeat bg-center cursor-pointer"
                              onClick={() => onEditItemClickHandler(itemValue)}
                            ></div>
                          )}
                          <div
                            style={{ backgroundImage: "url(/icons/times.svg)" }}
                            className="h-4 w-4 rounded-full bg-no-repeat bg-center bg-primary-200 cursor-pointer"
                            onClick={
                              disabled ? noop : () => onDeleteChipHandler(itemValue)
                            }
                          ></div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div
                  className={`flex w-full pl-2 ${multiple && values.length ? "h-auto" : "h-full"
                    }`}
                >
                  {values.length !== maxMultipleItems ? (
                    <>
                      <input
                        {...field}
                        ref={inputRef}
                        placeholder={placeHolder}
                        className={`disabled outline-none w-11/12 ${size === 'sm' ? 'text-xs' : ''} ${multiple && values.length ? 'mb-2' : ''}`}
                        disabled={disabled}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        value={userInput}
                        onFocus={onFocusInputHandler}
                        onBlur={onBlurInputHandler}
                        autoComplete="off"
                      />
                      {searchIcon && <SearchIcon className={`${size === 'sm' ? 'w-3' : 'w-4 my-3 mr-2'} `} />}
                      {userInput !== "" && fetchBy?.type &&
                        fetchBy?.type !== "carriers" &&
                        fetchBy?.type !== "conveyances" &&
                        fetchBy?.type !== "equipments" &&
                        fetchBy?.type !== "drivers" && !isLoading &&
                        !disabled && (
                          <div
                            className="w-2 my-3 mr-4 ml-1 text-gray-400 cursor-pointer"
                            onClick={clearValue}
                          >
                            <ClearInputIcon className="pointer-events-none" />
                          </div>
                        )}

                    </>
                  ) : null}
                  {(hasInputAsideErrors || error) &&
                    isTouched &&
                    !showOptions &&
                    showErrorIcon && (
                      <div className={`${size === 'sm' ? 'w-1/12' : 'w-1/12 mr-2'} self-center`}>
                        <div className={`${size === 'sm' ? 'w-2' : 'w-4'}`}>
                          <ErrorIcon />
                        </div>
                      </div>
                    )}
                </div>
              </div>
              {fetchBy?.type ? (
                isLoading && !userInput && (
                  <div className="absolute-center-vertically right-2">
                    <Spinner inverted={true} size="small" />
                  </div>
                )
              ) : (
                <div
                  id={`btn-toggle-options-${name}`}
                  className="cursor-pointer bg-white"
                  onClick={handleToggleOptions}
                >
                  <div className="flex">

                    {showOptions ? (
                      <div className={`${size === 'sm' ? 'w-2 my-2 mr-2 ml-1' : 'w-2 my-4 mr-4 ml-1'}`}>
                        <ArrowUpIcon className="pointer-events-none" />
                      </div>
                    ) : (
                      <div className={`${size === 'sm' ? 'w-2 my-2 mr-2 ml-1' : 'w-2 my-4 mr-4 ml-1'}`}>
                        <ArrowDownIcon className="pointer-events-none" />
                      </div>
                    )}
                    {userInput !== "" && !disabled && (
                      <div
                        className={`${size === 'sm' ? 'w-1/2 my-1 mr-2 text-gray-400' : 'w-2 my-3 mr-4 text-gray-400'} cursor-pointer`}
                        onClick={clearValue}
                      >
                        <ClearInputIcon className="pointer-events-none" />
                      </div>
                    )}

                  </div>
                </div>
              )}
            </div>
            {inputAside && <div className={`${size === 'sm' ? 'w-9' : 'w-9/12'}`}>{inputAside}</div>}
          </div>
          {renderOptionsList({
            userInput,
            filteredOptions,
            options: stateOptions,
            activeOption: state.activeOption
          })}
        </div>
      ) : (
        <span className="relative inline-block text-xs pt-3 pb-2">
          {inputAside ? `${userInput}-${inputAsideValue}` : userInput}
        </span>
      )}
      <div className="absolute">
        {!hasInputAsideErrors &&
          isTouched &&
          error &&
          !showOptions &&
          showErrorText && (
            <small className="text-red-400 text-2xs">{t(error)}</small>
          )}
        {hasInputAsideErrors && inputAsideErrorMessage && inputAsideErrorMessage()}
      </div>
    </div>
  );
};

SearchInput.defaultProps = {
  name: "",
  label: "",
  options: [],
  hintText: "",
  optionTextName: undefined,
  optionValueName: undefined,
  placeHolder: "Choose Port...",
  multiple: false,
  multipleInputProps: {
    maxItems: 2
  },
  enableLeadingButton: false,
  leadingButtonProps: {
    onClick: noop,
    label: ""
  },
  onChange: noop,
  onInputChange: undefined,
  onBlur: noop,
  icon: null,
  containerClassName: "",
  className: "",
  showErrorText: true,
  showErrorIcon: true,
  inputAside: null,
  inputAsideProps: {
    value: null,
    hasError: false,
    errorMessage: null
  },
  disabled: false,
  displayAliasText: false,
  usePhysicalTouched: false,
  labelButton: null,
  checkbox: null,
  showItemEditOption: false,
  onEditItemClickHandler: noop,
  onDeleteItemClickHandler: noop,
  readOnly: false,
  fetchBy: {
    type: null,
    dataKey: null,
    mapStrategy: noop,
    customSearchKey: null,
    customSearchValue: null
  },
  emptySearchMessage: "No options to show",
  defaultOption: null
};

export default memo(SearchInput);